import type { Directive } from 'vue';
import tippy, { type Instance, type Props as TippyProps } from 'tippy.js';

type TooltipContent = string | null | undefined;

type TooltipProps = Partial<{
  allowHTML: boolean;
  content: TooltipContent;
  delay: TippyProps['delay'];
  interactive: boolean;
  offset: TippyProps['offset'];
  placement: TippyProps['placement'];
}>;

type TooltipBindingValue = TooltipProps | TooltipContent;

type TooltipElement = HTMLElement & {
  // https://atomiks.github.io/tippyjs/v6/tippy-instance/#-property
  _tippy?: Instance;
};

function getTooltipPropsFromBindingValue(
  value: TooltipBindingValue,
): TooltipProps {
  if (typeof value === 'string' || value === null || value === undefined) {
    return { content: value };
  }

  return value;
}

function hasContent(tippyProps: Partial<TippyProps>) {
  return Boolean(tippyProps.content);
}

function omitUndefined<T extends Record<string, unknown>>(obj: T) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

function getTippyProps(value: TooltipBindingValue): Partial<TippyProps> {
  const tooltipProps = getTooltipPropsFromBindingValue(value);

  return omitUndefined({
    ignoreAttributes: true,
    theme: 'clearstream',
    allowHTML: tooltipProps.allowHTML,
    content: tooltipProps.content ?? undefined,
    delay: tooltipProps.delay,
    interactive: tooltipProps.interactive,
    offset: tooltipProps.offset,
    placement: tooltipProps.placement,
  });
}

function createTippy(el: TooltipElement, tippyProps: Partial<TippyProps>) {
  tippy(el, tippyProps);
}

const directive: Directive<TooltipElement, TooltipBindingValue> = {
  mounted(el, { value }) {
    const tippyProps = getTippyProps(value);

    if (!hasContent(tippyProps)) {
      return;
    }

    createTippy(el, tippyProps);
  },
  updated(el, { value }) {
    const tippyProps = getTippyProps(value);

    if (!hasContent(tippyProps)) {
      el._tippy?.destroy();
      return;
    }

    if (!el._tippy) {
      createTippy(el, tippyProps);
      return;
    }

    el._tippy.setProps(tippyProps);
  },
  unmounted(el) {
    el._tippy?.destroy();
  },
};

export default directive;
